/**
 * Voler Finder commerce adapter (Phase 1 — ATC timing only)
 * SNA-10 / unpublished theme candidate.
 *
 * Reads same-session Finder attribution from sessionStorage
 * (voler_finder_attribution_v2 — schema written by voler-finder-measurement.js)
 * and publishes CUSTOM Shopify customer events with bounded finder_* fields.
 *
 * IMPORTANT: Merchants cannot re-publish / mutate standard Shopify events
 * (product_added_to_cart, etc.). This adapter publishes voler:* customs only.
 * Customer Events / web pixel must subscribe to voler:product_added_to_cart
 * (and optional voler:product_viewed) to land finder_* in GA4. Google channel
 * continues to send standards without finder_*.
 *
 * Impact (theme.js): successful PDP/quick-buy ATC dispatches `variant:add`
 * on the product-form (bubbles:true), then `cart:change` on documentElement
 * with detail.baseEvent === "variant:add". This adapter listens to both.
 *
 * Fail closed. No PII. No purchase / cookie / pixel bridge (Phase 2).
 *
 * Preview QA: window.__volerFinderCommerceAdapter
 *   or append ?voler_adapter_debug=1 for console breadcrumbs (debug only).
 */
(function (global) {
  'use strict';

  var ATTRIBUTION_STORAGE_KEY = 'voler_finder_attribution_v2';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000; // match measurement.js
  var FORBIDDEN_FIELD = /(^|_)(email|name|customer|order|transaction|phone|address|full_url|free_text)($|_)/i;
  var SLUG = /^[a-z0-9][a-z0-9_-]{0,79}$/i;
  var VERSION = /^(bottoms|jersey)-finder-v[1-9][0-9]*$/;

  var viewedPublished = false;
  var lastAtcSignature = null;
  var lastAtcAt = 0;
  var bindDone = false;

  var debugQuery = false;
  try {
    debugQuery = !!(global.location && global.location.search &&
      /(?:^|[?&])voler_adapter_debug=1(?:&|$)/.test(global.location.search));
  } catch (_) { /* ignore */ }

  var debugState = {
    loaded: true,
    version: 'phase1-fix-20260910',
    listeners: [],
    lastEvent: null,
    lastPublish: null,
    lastSkipReason: null,
    atcCount: 0,
    viewCount: 0,
    lastDedupeAt: null
  };

  function setSkip(reason, extra) {
    debugState.lastSkipReason = reason;
    if (debugQuery) {
      try { console.info('[voler-finder-commerce-adapter] skip:', reason, extra || ''); } catch (_) { /* ignore */ }
    }
  }

  function setEvent(name, detail) {
    debugState.lastEvent = { name: name, at: Date.now(), detail: detail || null };
  }

  function now() {
    return Date.now();
  }

  function analyticsConsentAllowed() {
    try {
      return global.Shopify &&
        global.Shopify.customerPrivacy &&
        typeof global.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
        global.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  }

  function customerEventPublisher() {
    try {
      if (global.Shopify && global.Shopify.analytics && typeof global.Shopify.analytics.publish === 'function') {
        return global.Shopify.analytics.publish.bind(global.Shopify.analytics);
      }
    } catch (_) { /* fail closed */ }
    return null;
  }

  function readStoredAttribution() {
    try {
      var raw = global.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      if (!raw) return null;
      var value = JSON.parse(raw);
      if (!value || typeof value !== 'object') {
        global.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
        return null;
      }
      // Match measurement readStored: require expires_at and drop when expired.
      if (typeof value.expires_at !== 'number' || value.expires_at <= now()) {
        global.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
        return null;
      }
      return value;
    } catch (_) {
      try { global.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch (_) { /* no-op */ }
      return null;
    }
  }

  function boundedString(value, maxLen) {
    return typeof value === 'string' && value.length > 0 && value.length <= (maxLen || 120) && !/^https?:\/\//i.test(value);
  }

  function attributionContext() {
    if (!analyticsConsentAllowed()) {
      setSkip('consent_not_allowed');
      return null;
    }
    var record = readStoredAttribution();
    if (!record) {
      setSkip('no_attribution_marker');
      return null;
    }

    var ctx = { finder_assisted: true };

    if (boundedString(record.finder_category, 40) && SLUG.test(String(record.finder_category))) {
      ctx.finder_category = record.finder_category;
    } else if (boundedString(record.finder_category, 40)) {
      // Allow category labels like "bottoms" / "jerseys" even if hyphenated.
      ctx.finder_category = String(record.finder_category).slice(0, 40);
    }

    if (boundedString(record.finder_version, 40) && VERSION.test(record.finder_version)) {
      ctx.finder_version = record.finder_version;
    } else if (boundedString(record.finder_version, 40)) {
      ctx.finder_version = String(record.finder_version).slice(0, 40);
    }

    if (boundedString(record.finder_session_id, 80)) {
      ctx.finder_session_id = record.finder_session_id;
    }
    if (boundedString(record.selected_product_key, 80)) {
      ctx.selected_product_key = record.selected_product_key;
    }
    if (boundedString(record.selected_product_handle, 120)) {
      ctx.selected_product_handle = record.selected_product_handle;
    }

    // Require at least category or version so empty/corrupt markers do nothing.
    if (!ctx.finder_category && !ctx.finder_version) {
      setSkip('attribution_missing_category_and_version');
      return null;
    }

    // Reject if any unexpected forbidden keys leaked into ctx.
    for (var key in ctx) {
      if (Object.prototype.hasOwnProperty.call(ctx, key) && FORBIDDEN_FIELD.test(key)) {
        setSkip('forbidden_field:' + key);
        return null;
      }
    }
    return ctx;
  }

  function publishCustom(eventName, extra) {
    var publish = customerEventPublisher();
    if (!publish) {
      setSkip('publish_api_unavailable');
      return false;
    }
    var ctx = attributionContext();
    if (!ctx) return false;
    var payload = {};
    var k;
    for (k in ctx) {
      if (Object.prototype.hasOwnProperty.call(ctx, k)) payload[k] = ctx[k];
    }
    if (extra && typeof extra === 'object') {
      for (k in extra) {
        if (!Object.prototype.hasOwnProperty.call(extra, k)) continue;
        if (FORBIDDEN_FIELD.test(k)) continue;
        var v = extra[k];
        if (v === null || typeof v === 'boolean') payload[k] = v;
        else if (typeof v === 'number' && isFinite(v)) payload[k] = v;
        else if (boundedString(v, 120)) payload[k] = v;
      }
    }
    try {
      publish(eventName, payload);
      debugState.lastPublish = { event: eventName, at: Date.now(), payload: payload };
      debugState.lastSkipReason = null;
      if (debugQuery) {
        try { console.info('[voler-finder-commerce-adapter] published', eventName, payload); } catch (_) { /* ignore */ }
      }
      return true;
    } catch (_) {
      setSkip('publish_threw');
      return false;
    }
  }

  function handleAtc(sourceTiming, event) {
    try {
      setEvent(sourceTiming === 'impact_cart_change' ? 'cart:change' : 'variant:add', {
        sourceTiming: sourceTiming,
        baseEvent: event && event.detail && event.detail.baseEvent || null
      });

      var ctx = attributionContext();
      if (!ctx) return;

      // Light de-dupe: Impact fires variant:add then cart:change(baseEvent=variant:add).
      // cart:change detail has no items[], so signature must ignore per-event item ids.
      var sig = [
        ctx.finder_session_id || '',
        ctx.selected_product_key || ctx.selected_product_handle || '',
        ctx.finder_version || '',
        'atc'
      ].join('|');
      var t = now();
      if (sig === lastAtcSignature && (t - lastAtcAt) < 1500) {
        // Keep lastPublish; note dedupe without clobbering a successful publish reason.
        debugState.lastDedupeAt = t;
        if (debugQuery) {
          try { console.info('[voler-finder-commerce-adapter] deduped atc'); } catch (_) { /* ignore */ }
        }
        return;
      }
      lastAtcSignature = sig;
      lastAtcAt = t;

      if (publishCustom('voler:product_added_to_cart', { source_timing: sourceTiming })) {
        debugState.atcCount += 1;
      }
    } catch (_) {
      setSkip('atc_handler_threw');
    }
  }

  function onVariantAdd(event) {
    handleAtc('impact_variant_add', event);
  }

  function onCartChange(event) {
    try {
      var base = event && event.detail && event.detail.baseEvent;
      // Only decorate ATC-originated cart changes, not line-item qty edits.
      if (base !== 'variant:add') return;
      handleAtc('impact_cart_change', event);
    } catch (_) {
      setSkip('cart_change_handler_threw');
    }
  }

  function maybePublishProductViewed() {
    try {
      if (viewedPublished) return;
      var pageType = null;
      try {
        pageType = global.themeVariables && global.themeVariables.settings && global.themeVariables.settings.pageType;
      } catch (_) { /* ignore */ }
      var path = (global.location && global.location.pathname) || '';
      var isProduct = pageType === 'product' || /^\/products\//.test(path);
      if (!isProduct) {
        setSkip('not_product_page');
        return;
      }
      if (!attributionContext()) return;
      // Mark only after a successful publish so consent/publisher races can retry.
      if (publishCustom('voler:product_viewed', { source_timing: 'product_page_load' })) {
        viewedPublished = true;
        debugState.viewCount += 1;
      }
    } catch (_) {
      setSkip('view_handler_threw');
    }
  }

  function scheduleViewRetries() {
    // Consent + Shopify.analytics often become ready after first paint.
    var attempts = 0;
    var maxAttempts = 20;
    var timer = global.setInterval(function () {
      attempts += 1;
      if (viewedPublished || attempts >= maxAttempts) {
        global.clearInterval(timer);
        return;
      }
      maybePublishProductViewed();
    }, 250);
  }

  function bind() {
    if (bindDone || !global.document) return;
    bindDone = true;

    // Capture on document: Impact dispatches variant:add on product-form with bubbles:true.
    // Capture catches the event on the way down; cart:change is a backup (baseEvent filter).
    global.document.addEventListener('variant:add', onVariantAdd, true);
    global.document.addEventListener('cart:change', onCartChange, true);
    debugState.listeners = ['variant:add@capture', 'cart:change@capture'];

    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', function () {
        maybePublishProductViewed();
        scheduleViewRetries();
      }, { once: true });
    } else {
      maybePublishProductViewed();
      scheduleViewRetries();
    }

    try {
      global.document.addEventListener('visitorConsentCollected', function () {
        maybePublishProductViewed();
      });
    } catch (_) { /* ignore */ }

    if (debugQuery) {
      try { console.info('[voler-finder-commerce-adapter] bound', debugState.listeners); } catch (_) { /* ignore */ }
    }
  }

  try {
    global.__volerFinderCommerceAdapter = debugState;
  } catch (_) { /* ignore */ }

  if (global.document) {
    bind();
  }
})(typeof window !== 'undefined' ? window : this);
